import { Add, Block, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DateTime } from 'luxon';

interface CalendarHeaderProps {
    month: number; // 1-12
    year: number;
    onPreviousMonth: () => void;
    onNextMonth: () => void;
    onAddBlockedDate: () => void;
    onAddBooking: () => void;
}

const CalendarHeader = ({
    month,
    year,
    onPreviousMonth,
    onNextMonth,
    onAddBlockedDate,
    onAddBooking,
}: CalendarHeaderProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const monthName = DateTime.local(year, month).toFormat(isMobile ? 'LLL yyyy' : 'LLLL yyyy');

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                p: isMobile ? 1 : 2,
                backgroundColor: '#1a1a2e',
                border: '3px solid #6366f1',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: isMobile ? 1 : 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
                <IconButton
                    onClick={onPreviousMonth}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: 'rgba(99, 102, 241, 0.2)',
                            borderColor: '#6366f1',
                        },
                    }}
                    aria-label="Previous month"
                >
                    <ChevronLeft fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
                <Typography
                    variant={isMobile ? 'body1' : 'h5'}
                    sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: isMobile ? 'auto' : '220px',
                        textAlign: 'center',
                        px: isMobile ? 1 : 0,
                    }}
                >
                    {monthName}
                </Typography>
                <IconButton
                    onClick={onNextMonth}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: 'rgba(99, 102, 241, 0.2)',
                            borderColor: '#6366f1',
                        },
                    }}
                    aria-label="Next month"
                >
                    <ChevronRight fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2 }}>
                {/* Icon-only buttons on mobile */}
                {isMobile ? (
                    <>
                        <Tooltip title="New Booking">
                            <IconButton
                                onClick={onAddBooking}
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'secondary.main',
                                    borderRadius: 0,
                                    color: 'secondary.main',
                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                    },
                                }}
                                aria-label="New Booking"
                            >
                                <Add />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Add Blocked Date">
                            <IconButton
                                onClick={onAddBlockedDate}
                                sx={{
                                    border: '2px solid #374151',
                                    borderRadius: 0,
                                    color: '#fff',
                                    '&:hover': {
                                        borderColor: '#6366f1',
                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    },
                                }}
                                aria-label="Add Blocked Date"
                            >
                                <Block />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<Add />}
                            onClick={onAddBooking}
                            sx={{
                                borderRadius: 0,
                                border: '2px solid',
                                borderColor: 'secondary.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                            }}
                        >
                            New Booking
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onAddBlockedDate}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                },
                            }}
                        >
                            Add Blocked Date
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default CalendarHeader;
